export const CLASS_GRADE_LEVELS = [
  { number: 1, label: "Grade 1", fields: ["grade_level1", "gradeLevel1"] },
  { number: 2, label: "Grade 2", fields: ["grade_level2", "gradeLevel2"] },
  { number: 3, label: "Grade 3", fields: ["grade_level3", "gradeLevel3"] },
  { number: 4, label: "Grade 4", fields: ["grade_level4", "gradeLevel4"] },
  { number: 5, label: "Grade 5", fields: ["grade_level5", "gradeLevel5"] },
  { number: 6, label: "Grade 6", fields: ["grade_level6", "gradeLevel6"] },
  { number: 7, label: "Grade 7", fields: ["grade_level7", "gradeLevel7"] },
  { number: 8, label: "Grade 8", fields: ["grade_level8", "gradeLevel8"] }
];

const normaliseComparableText = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const getClassSources = (classItem) =>
  [classItem, classItem?.class].filter((source) => source && typeof source === "object");

const isSelectedGradeFlag = (value) =>
  value === true || value === 1 || value === "1" || String(value).toLowerCase() === "true";

const firstPresentValue = (sources, fields) => {
  for (const source of sources) {
    for (const field of fields) {
      const value = source?.[field];

      if (value !== undefined && value !== null && String(value).trim() !== "") {
        return value;
      }
    }
  }

  return "";
};

const getConfiguredGradeLabel = (gradeNumber, countryGrades) =>
  String(
    countryGrades?.[`grade${gradeNumber}`] ||
      countryGrades?.[`grade_level${gradeNumber}`] ||
      ""
  ).trim();

export const getConfiguredGradeOptions = (countryGrades, maximumGrade = 3) =>
  CLASS_GRADE_LEVELS.filter((grade) => grade.number <= maximumGrade)
    .map((grade) => ({
      number: grade.number,
      value: `grade_level${grade.number}`,
      label: getConfiguredGradeLabel(grade.number, countryGrades)
    }))
    .filter((grade) => grade.label);

export const getClassGradeNumber = (classItem) => {
  const sources = getClassSources(classItem);
  const flaggedGrade = CLASS_GRADE_LEVELS.find((grade) =>
    sources.some((source) => grade.fields.some((field) => isSelectedGradeFlag(source?.[field])))
  );

  if (flaggedGrade) {
    return flaggedGrade.number;
  }

  const explicitGradeNumber = firstPresentValue(sources, [
    "gradeLevelNumber",
    "grade_level_number",
    "gradeLevel",
    "grade_level"
  ]);
  const match = String(explicitGradeNumber || "").match(/^(?:grade[_ ]?level[_ ]?)?(\d)$/i);

  return match ? Number(match[1]) : null;
};

export const getGradeLevelLabel = (value, countryGrades) => {
  const text = String(value || "").trim();
  const gradeNumberMatch = text.match(/^(?:grade[_ ]?level[_ ]?)?(\d)$/i);
  const gradeNumber = gradeNumberMatch ? Number(gradeNumberMatch[1]) : Number.NaN;

  return (
    getConfiguredGradeLabel(gradeNumber, countryGrades) ||
    CLASS_GRADE_LEVELS.find((grade) => grade.number === gradeNumber)?.label ||
    text
  );
};

export const getClassGradeLabel = (classItem, countryGrades) => {
  const sources = getClassSources(classItem);
  const flaggedGrade = CLASS_GRADE_LEVELS.find((grade) =>
    sources.some((source) => grade.fields.some((field) => isSelectedGradeFlag(source?.[field])))
  );
  const configuredLabel = flaggedGrade
    ? getConfiguredGradeLabel(flaggedGrade.number, countryGrades)
    : "";

  if (configuredLabel) {
    return configuredLabel;
  }

  const explicitGrade = firstPresentValue(sources, [
    "grade",
    "gradeName",
    "grade_name",
    "gradeLabel",
    "grade_label",
    "gradeLevelLabel",
    "grade_level_label",
    "gradeLevel",
    "grade_level",
    "gradeLevelNumber",
    "grade_level_number"
  ]);

  if (explicitGrade !== "") {
    return getGradeLevelLabel(explicitGrade, countryGrades);
  }

  return flaggedGrade?.label || "";
};

export const getClassAcademicYear = (classItem) =>
  String(
    firstPresentValue(getClassSources(classItem), [
      "academicYear",
      "academic_year",
      "academicyear",
      "academicYearLabel",
      "schoolYear",
      "school_year",
      "yearLabel"
    ]) || ""
  ).trim();

export const getClassName = (classItem) =>
  String(
    firstPresentValue([classItem], ["className", "class_name", "classname"]) ||
      firstPresentValue([classItem?.class], ["className", "class_name", "classname", "name"]) ||
      firstPresentValue([classItem], ["name"]) ||
      ""
  ).trim();

export const getDistinctClassGradeLabel = (
  classItem,
  className = getClassName(classItem),
  countryGrades
) => {
  const grade = getClassGradeLabel(classItem, countryGrades);
  return normaliseComparableText(grade) === normaliseComparableText(className) ? "" : grade;
};

export const formatClassDisplayName = (
  classItem,
  fallback = "Class unavailable",
  countryGrades
) => {
  const providedDisplayName = String(
    firstPresentValue(getClassSources(classItem), ["classDisplayName", "class_display_name"]) || ""
  ).trim();
  const className = getClassName(classItem);
  const base = className || providedDisplayName;
  const grade = getDistinctClassGradeLabel(classItem, base || className, countryGrades);
  const academicYear = getClassAcademicYear(classItem);
  const parts = [base];

  [grade, academicYear].forEach((value) => {
    const comparableValue = normaliseComparableText(value);
    const comparableDisplay = normaliseComparableText(parts.join(" "));

    if (value && comparableValue && !comparableDisplay.includes(comparableValue)) {
      parts.push(value);
    }
  });

  return parts.filter(Boolean).join(" - ") || fallback;
};
